import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import client, { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, KeyRound, Eye, EyeOff, Building2, Users, ArrowLeft, ArrowRight, Check } from 'lucide-react';

const ROLE_LABEL = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Company logo banner shown on invitation screens so joining feels on-brand.
const CompanyBrand = ({ branding }) => (branding?.has_logo ? (
  <div className="mb-4 flex items-center gap-3 rounded-xl border bg-muted/30 p-3" data-testid="signup-company-brand">
    <img src={`${API}/tenant/logo/${branding.tenant_id}?v=${branding.logo_version || 0}`} alt={branding.company_name} className="h-10 w-10 shrink-0 rounded-lg border bg-white object-contain" data-testid="signup-company-logo" />
    <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Joining</div><div className="truncate font-display font-semibold" data-testid="signup-company-name">{branding.company_name}</div></div>
  </div>
) : null);

const Shell = ({ children }) => (
  <div className="relative flex min-h-[100dvh] items-start justify-center overflow-y-auto bg-[hsl(var(--foreground))] p-4 sm:items-center">
    <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(1000px_circle_at_20%_0%,rgba(245,179,1,0.18),transparent_55%),radial-gradient(800px_circle_at_90%_20%,rgba(37,99,235,0.22),transparent_55%)]" />
    <div className="relative z-10 my-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
      <div className="mb-6 flex justify-center"><Logo /></div>
      {children}
    </div>
  </div>
);

export default function Signup() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const googleMode = params.get('google') === '1';
  const navigate = useNavigate();
  const { setUser, finishAuth } = useAuth();

  const [mode, setMode] = useState(token ? 'token' : googleMode ? 'google' : 'choose');
  const [invite, setInvite] = useState(null);        // token-based invite details
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // shared fields
  const [name, setName] = useState(() => { try { return JSON.parse(localStorage.getItem('nf_company_draft') || '{}').name || ''; } catch { return ''; } });
  const [email, setEmail] = useState(() => { try { return JSON.parse(localStorage.getItem('nf_company_draft') || '{}').email || ''; } catch { return ''; } });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);     // role/email from verified code
  const [gpend, setGpend] = useState(null);           // google pending {temp_token,email,name}
  const [showPw, setShowPw] = useState(false);

  // New Company wizard (draft auto-saved to localStorage so data survives errors/reloads)
  const [step, setStep] = useState(1);
  const [co, setCo] = useState(() => {
    try { return { company_name: '', legal_name: '', ein: '', dot_number: '', mc_number: '', ...(JSON.parse(localStorage.getItem('nf_company_draft') || '{}').co || {}) }; }
    catch { return { company_name: '', legal_name: '', ein: '', dot_number: '', mc_number: '' }; }
  });

  // Persist the New Company draft (never the password) on every change
  useEffect(() => {
    if (mode === 'company' || mode === 'choose') {
      try { localStorage.setItem('nf_company_draft', JSON.stringify({ co, name, email })); } catch {}
    }
  }, [co, name, email, mode]);

  useEffect(() => {
    if (token) {
      client.get(`/invites/lookup/${token}`)
        .then((r) => { setInvite(r.data); setName(r.data.name || ''); })
        .catch(() => setError('This invitation is invalid or has expired.'));
    } else if (googleMode) {
      try {
        const p = JSON.parse(sessionStorage.getItem('hibret_gpend') || 'null');
        if (!p?.temp_token) { setError('Your Google session expired. Please sign in with Google again.'); }
        else { setGpend(p); setEmail(p.email || ''); setName(p.name || ''); }
      } catch { setError('Your Google session expired. Please sign in with Google again.'); }
    }
  }, [token, googleMode]);

  const verifyCode = async () => {
    if (!code.trim()) { toast.error('Enter your invitation code'); return; }
    try {
      const r = await client.get(`/invites/lookup-code/${encodeURIComponent(code.trim().toUpperCase())}`);
      setCodeInfo(r.data);
      if (!email && r.data.email) setEmail(r.data.email);
      if (!name && r.data.name) setName(r.data.name);
      toast.success(`Code valid — invited as ${ROLE_LABEL(r.data.role)}`);
    } catch (e) { setCodeInfo(null); toast.error(e?.response?.data?.detail || 'Invalid invitation code'); }
  };

  const submitToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await client.post('/auth/register', { token, password, name });
      finishAuth(res.data); toast.success('Account created'); navigate('/dashboard');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!email || !code || !password) { toast.error('Email, code and password are required'); return; }
    setLoading(true);
    try {
      const res = await client.post('/auth/register', { email, code: code.trim().toUpperCase(), password, name });
      finishAuth(res.data); toast.success('Account created'); navigate('/dashboard');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const submitGoogle = async (e) => {
    e.preventDefault();
    if (!code) { toast.error('Enter your invitation code'); return; }
    setLoading(true);
    try {
      const res = await client.post('/auth/google/redeem', { temp_token: gpend.temp_token, code: code.trim().toUpperCase() });
      sessionStorage.removeItem('hibret_gpend');
      finishAuth(res.data); toast.success('Welcome!'); navigate('/dashboard');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Invalid invitation code'); }
    finally { setLoading(false); }
  };

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!email || !password || !co.company_name) { toast.error('Company name, email and password are required'); return; }
    setLoading(true);
    try {
      const res = await client.post('/auth/register-company', { ...co, admin_name: name, email, password });
      localStorage.removeItem('nf_company_draft');
      await finishAuth(res.data);
      toast.success('Company workspace created! Choose a plan to get started.');
      navigate('/plans');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create company'); }
    finally { setLoading(false); }
  };

  if (error) return <Shell><div className="text-center"><p className="text-sm text-[hsl(var(--danger))]">{error}</p><Button variant="outline" className="mt-4" onClick={() => navigate('/login')} data-testid="signup-back-button">Back to login</Button></div></Shell>;

  // Mode: token invitation link
  if (mode === 'token') {
    if (!invite) return <Shell><div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></Shell>;
    return (
      <Shell>
        <CompanyBrand branding={invite.branding} />
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--success))]/10 p-3 text-sm text-[hsl(var(--success))]"><ShieldCheck className="h-4 w-4" /> Invited as <b>{ROLE_LABEL(invite.role)}</b></div>
        <h1 className="font-display text-2xl font-semibold">Accept invitation</h1>
        <p className="mb-6 text-sm text-muted-foreground">{invite.email}</p>
        <form onSubmit={submitToken} className="space-y-4">
          <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="signup-name-input" required /></div>
          <div><Label>Create password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="signup-password-input" required minLength={6} />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="signup-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="signup-submit-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create account</Button>
        </form>
      </Shell>
    );
  }

  // Mode: complete Google sign-in with code
  if (mode === 'google') {
    return (
      <Shell>
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))]/10 p-3 text-sm text-[hsl(var(--primary))]"><KeyRound className="h-4 w-4" /> One more step for <b>{email}</b></div>
        <h1 className="font-display text-2xl font-semibold">Enter invitation code</h1>
        <p className="mb-6 text-sm text-muted-foreground">NuxFleet is invitation-only. Enter the code sent to you by your company admin.</p>
        <form onSubmit={submitGoogle} className="space-y-4">
          <div><Label>Invitation code</Label><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="HIB-XXXXX" className="mt-1 font-mono tracking-widest" data-testid="signup-code-input" required /></div>
          <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="signup-google-submit-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Continue</Button>
        </form>
      </Shell>
    );
  }

  // Mode: choose signup path (New Company vs Invitation Code)
  if (mode === 'choose') {
    return (
      <Shell>
        <h1 className="text-center font-display text-2xl font-semibold">Get started with NuxFleet</h1>
        <p className="mb-6 mt-1 text-center text-sm text-muted-foreground">How would you like to join?</p>
        <div className="space-y-3">
          <button onClick={() => { setMode('company'); setStep(1); }} className="group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/5" data-testid="signup-choose-company">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]"><Building2 className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1"><div className="font-semibold">Create a new company</div><div className="text-xs text-muted-foreground">Set up a fresh workspace and become the company admin.</div></div>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
          </button>
          <button onClick={() => setMode('code')} className="group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5" data-testid="signup-choose-invite">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"><Users className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1"><div className="font-semibold">Join with an invitation code</div><div className="text-xs text-muted-foreground">Your company admin invited you to an existing workspace.</div></div>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
        <button type="button" onClick={() => navigate('/login')} className="mt-6 w-full text-center text-xs text-muted-foreground hover:text-foreground" data-testid="signup-to-login-button">Already have an account? Sign in</button>
      </Shell>
    );
  }

  // Mode: New Company wizard
  if (mode === 'company') {
    const next = (e) => { e.preventDefault(); if (!co.company_name.trim()) { toast.error('Company name is required'); return; } setStep(2); };
    return (
      <Shell>
        <button type="button" onClick={() => (step === 2 ? setStep(1) : setMode('choose'))} className="mb-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="signup-company-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="mb-5 flex items-center gap-2">
          {[1, 2].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? 'bg-[hsl(var(--gold))]' : 'bg-muted'}`} />
          ))}
        </div>
        {step === 1 ? (
          <form onSubmit={next} className="space-y-4">
            <div><h1 className="font-display text-2xl font-semibold">Company details</h1><p className="text-sm text-muted-foreground">Tell us about your trucking company.</p></div>
            <div><Label>Company name *</Label><Input value={co.company_name} onChange={(e) => setCo({ ...co, company_name: e.target.value })} placeholder="Acme Trucking" className="mt-1" data-testid="company-name-input" required /></div>
            <div><Label>Legal name</Label><Input value={co.legal_name} onChange={(e) => setCo({ ...co, legal_name: e.target.value })} placeholder="Acme Trucking LLC" className="mt-1" data-testid="company-legal-input" /></div>
            <div><Label>EIN (Tax ID)</Label><Input value={co.ein} onChange={(e) => setCo({ ...co, ein: e.target.value })} placeholder="12-3456789" className="mt-1" data-testid="company-ein-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>USDOT #</Label><Input value={co.dot_number} onChange={(e) => setCo({ ...co, dot_number: e.target.value })} placeholder="1234567" className="mt-1" data-testid="company-dot-input" /></div>
              <div><Label>MC #</Label><Input value={co.mc_number} onChange={(e) => setCo({ ...co, mc_number: e.target.value })} placeholder="MC-987654" className="mt-1" data-testid="company-mc-input" /></div>
            </div>
            <Button type="submit" className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="company-next-button">Continue <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </form>
        ) : (
          <form onSubmit={submitCompany} className="space-y-4">
            <div><h1 className="font-display text-2xl font-semibold">Your admin account</h1><p className="text-sm text-muted-foreground">You'll be the Company Admin for <b>{co.company_name}</b>.</p></div>
            <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="signup-name-input" required /></div>
            <div><Label>Work email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="signup-email-input" required /></div>
            <div><Label>Create password</Label>
              <div className="relative mt-1">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="signup-password-input" required minLength={6} />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="signup-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="company-create-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Create company</Button>
          </form>
        )}
      </Shell>
    );
  }

  // Mode: manual code entry sign-up
  return (
    <Shell>
      <button type="button" onClick={() => setMode('choose')} className="mb-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="signup-code-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <div className="mb-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))]/10 p-3 text-sm text-[hsl(var(--primary))]"><KeyRound className="h-4 w-4" /> Invitation-only sign up</div>
      <h1 className="font-display text-2xl font-semibold">Create your account</h1>
      <p className="mb-6 text-sm text-muted-foreground">Enter the invitation code sent to your email.</p>
      <form onSubmit={submitCode} className="space-y-4">
        <div>
          <Label>Invitation code</Label>
          <div className="mt-1 flex gap-2">
            <Input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeInfo(null); }} placeholder="HIB-XXXXX" className="font-mono tracking-widest" data-testid="signup-code-input" required />
            <Button type="button" variant="outline" onClick={verifyCode} data-testid="signup-verify-code-button">Verify</Button>
          </div>
          {codeInfo && <p className="mt-1 text-xs text-[hsl(var(--success))]">Valid — role: {ROLE_LABEL(codeInfo.role)}</p>}
        </div>
        {codeInfo?.branding?.has_logo && <CompanyBrand branding={codeInfo.branding} />}
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="signup-email-input" required /></div>
        <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="signup-name-input" required /></div>
        <div><Label>Create password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="signup-password-input" required minLength={6} /></div>
        <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="signup-submit-button">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create account</Button>
        <button type="button" onClick={() => navigate('/login')} className="w-full text-center text-xs text-muted-foreground hover:text-foreground" data-testid="signup-to-login-button">Already have an account? Sign in</button>
      </form>
    </Shell>
  );
}
